import React, { Suspense } from 'react';
import { lazyImport } from './utils/lazyImport';
import type { ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';

// Layout
const Layout = lazyImport(() => import('./components/Layout'));

// Lazy Load Pages
const Dashboard = lazyImport(() => import('./pages/Dashboard'));
const RADashboard = lazyImport(() => import('./pages/RADashboard').then(m => ({ default: m.RADashboard })));
const AcquisitionDashboard = lazyImport(() => import('./pages/dashboard/AcquisitionDashboard'));
const CompanyList = lazyImport(() => import('./pages/companies/CompanyList'));
const CompanyDetailPage = lazyImport(() => import('./pages/companies/CompanyDetailPage'));
const CompanyForm = lazyImport(() => import('./pages/companies/CompanyForm'));
const JobList = lazyImport(() => import('./pages/jobs/JobList'));
const JobDetailPage = lazyImport(() => import('./pages/jobs/JobDetailPage'));
const JobPrintPage = lazyImport(() => import('./pages/jobs/JobPrintPage'));
const JobForm = lazyImport(() => import('./pages/jobs/JobForm'));
const SlotMaintenancePage = lazyImport(() => import('./pages/jobs/SlotMaintenancePage'));
const CandidateList = lazyImport(() => import('./pages/candidates/CandidateList'));
const CandidateDetailPage = lazyImport(() => import('./pages/candidates/CandidateDetailPage'));
const CandidateForm = lazyImport(() => import('./pages/candidates/CandidateForm'));
const KanbanView = lazyImport(() => import('./pages/candidates/KanbanView'));
const StaffList = lazyImport(() => import('./pages/staff/StaffList'));
const StaffForm = lazyImport(() => import('./pages/staff/StaffForm'));
const ApplicationForm = lazyImport(() => import('./pages/applications/ApplicationForm'));
const ApplicationList = lazyImport(() => import('./pages/applications/ApplicationList'));
const ApplicationDetailPage = lazyImport(() => import('./pages/applications/ApplicationDetailPage'));
const MyPage = lazyImport(() => import('./pages/MyPage'));
const ImportPage = lazyImport(() => import('./pages/ImportPage'));
const MaintenancePage = lazyImport(() => import('./pages/MaintenancePage'));
// UltimateImportPage is a named export
const UltimateImportPage = lazyImport(() => import('./features/ultimate-import/UltimateImportPage').then(module => ({ default: module.UltimateImportPage })));
const UltimateResumePage = lazyImport(() => import('./features/resume/UltimateResumePage').then(module => ({ default: module.UltimateResumePage })));
const YomiDashboard = lazyImport(() => import('./pages/yomi/YomiDashboard').then(m => ({ default: m.YomiDashboard })));
const ApplicationDocumentStudioPage = lazyImport(() => import('./pages/candidates/ApplicationDocumentStudioPage'));

const LoginPage = lazyImport(() => import('./pages/LoginPage'));
const PublicEntryPage = lazyImport(() => import('./pages/public/PublicEntryPage'));
const EmbeddedEntryPage = lazyImport(() => import('./pages/public/EmbeddedEntryPage').then(module => ({ default: module.EmbeddedEntryPage })));
const ScreeningFormPage = lazyImport(() => import('./pages/public/ScreeningFormPage'));
const EntryThanksPage = lazyImport(() => import('./pages/public/EntryThanksPage').then(module => ({ default: module.EntryThanksPage })));
const PublicInterviewRoomPage = lazyImport(() => import('./pages/public/PublicInterviewRoomPage').then(module => ({ default: module.PublicInterviewRoomPage })));

const DailyDigestArchivePage = lazyImport(() => import('./pages/DailyDigestArchivePage'));
const DailyReportPage = lazyImport(() => import('./pages/DailyReportPage').then(module => ({ default: module.DailyReportPage })));
const EmbedSettingsPage = lazyImport(() => import('./pages/admin/EmbedSettingsPage'));
const MasterDataPage = lazyImport(() => import('./pages/admin/MasterDataPage'));
const FormApplicationLogsPage = lazyImport(() => import('./pages/admin/FormApplicationLogsPage'));
const KpiBudgetAdminPage = lazyImport(() => import('./pages/admin/KpiBudgetAdminPage'));
const AdvertisingAnalytics = lazyImport(() => import('./pages/dashboard/AdvertisingAnalytics'));
const RegistrationAnalysisPage = lazyImport(() => import('./pages/RegistrationAnalysisPage'));
const AiAnalyticsPage = lazyImport(() => import('./pages/AiAnalyticsPage'));
const FacilityMapSearchPage = lazyImport(() => import('./pages/FacilityMapSearchPage').then(module => ({ default: (module as any).FacilityMapSearchPage || (module as any).default })));




// Loading Component
const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-canvas">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      <p className="text-ink-muted text-sm font-medium">読み込み中...</p>
    </div>
  </div>
);

// Protected Route Component (Checks Auth)
const PrivateRoute = () => {
  const { currentUser, loading } = useAuth();
  if (loading) return <Loading />;
  return currentUser ? <Outlet /> : <Navigate to="/login" replace />;
};

// Admin Route Component (Checks Role)
const AdminRoute = ({ children }: { children: ReactNode }) => {
  const { isAdmin, loading } = useAuth();
  if (loading) return null;
  if (!isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
};

function App() {
  return (
    <Router>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route element={<PrivateRoute />}>
            <Route path="/" element={<Layout />}>
              <Route index element={<Dashboard />} />
              <Route path="ca-dashboard" element={<Dashboard />} />
              <Route path="ra-dashboard" element={<RADashboard />} />
              <Route path="acquisition" element={<AcquisitionDashboard />} />
              <Route path="mypage" element={<MyPage />} />
              <Route path="companies" element={<CompanyList />} />
              <Route path="companies/new" element={<CompanyForm />} />
              <Route path="companies/:id" element={<CompanyDetailPage />} />
              <Route path="companies/:id/edit" element={<CompanyForm />} />
              <Route path="jobs" element={<JobList />} />
              <Route path="jobs/new" element={<JobForm />} />
              <Route path="jobs/slot-maintenance" element={<SlotMaintenancePage />} />
              <Route path="jobs/:id" element={<JobDetailPage />} />

              <Route path="jobs/:id/edit" element={<JobForm />} />
              <Route path="candidates" element={<CandidateList />} />
              <Route path="candidates/kanban" element={<KanbanView />} />
              <Route path="candidates/new" element={<CandidateForm />} />
              <Route path="candidates/:id" element={<CandidateDetailPage />} />
              <Route path="candidates/:id/edit" element={<CandidateForm />} />

              <Route path="yomi" element={<YomiDashboard />} />

              {/* Protected Staff Routes */}
              <Route path="staff" element={<AdminRoute><StaffList /></AdminRoute>} />
              <Route path="staff/new" element={<AdminRoute><StaffForm /></AdminRoute>} />
              <Route path="staff/:id" element={<AdminRoute><StaffForm /></AdminRoute>} />

              <Route path="applications" element={<ApplicationList />} />
              <Route path="applications/new" element={<ApplicationForm />} />
              <Route path="applications/:id" element={<ApplicationDetailPage />} />
              <Route path="applications/:id/edit" element={<ApplicationForm />} />


              <Route path="daily-digest" element={<DailyReportPage />} />
              <Route path="daily-reports" element={<DailyReportPage />} />
              <Route path="daily-digest/archive" element={<DailyDigestArchivePage />} />
              <Route path="ai-analytics" element={<AiAnalyticsPage />} />
              <Route path="/import-page" element={<ImportPage />} />
              <Route path="ultimate-import" element={<UltimateImportPage />} />
              <Route path="maintenance" element={<AdminRoute><MaintenancePage /></AdminRoute>} />
              <Route path="admin/kpi-budget" element={<AdminRoute><KpiBudgetAdminPage /></AdminRoute>} />
              <Route path="embed-settings" element={<AdminRoute><EmbedSettingsPage /></AdminRoute>} />
              <Route path="master-data" element={<AdminRoute><MasterDataPage /></AdminRoute>} />
              <Route path="form-application-logs" element={<AdminRoute><FormApplicationLogsPage /></AdminRoute>} />
              <Route path="advertising-analytics" element={<AdminRoute><AdvertisingAnalytics /></AdminRoute>} />
              <Route path="registration-analysis" element={<AdminRoute><RegistrationAnalysisPage /></AdminRoute>} />

            </Route>

            {/* Full-screen Pages (No Layout) - 求職者提示・印刷・スタジオ */}
            <Route path="/facility-map" element={<FacilityMapSearchPage />} />
            <Route path="/jobs/:id/print" element={<JobPrintPage />} />
            <Route path="/candidates/:id/resume" element={<UltimateResumePage />} />
            <Route path="/candidates/:id/studio" element={<ApplicationDocumentStudioPage />} />
          </Route>

          {/* Public Routes - ACCESSIBLE WITHOUT LOGIN */}
          <Route path="/entry" element={<PublicEntryPage />} />
          <Route path="/entry/embed" element={<EmbeddedEntryPage />} />
          <Route path="/entry/thanks/:type" element={<EntryThanksPage />} />
          <Route path="/screening" element={<ScreeningFormPage />} />
          <Route path="/interview-room/:token" element={<PublicInterviewRoomPage />} />

        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
